import React, { createContext, useContext, useState } from 'react';

/**
 * Bridges the Dashboard's active view (Discover / MCP Servers / Agents / ...)
 * with the Sidebar navigation, which lives in Layout (Dashboard's parent).
 *
 * The Dashboard used to own `viewFilter` as local state and render the tabs
 * itself. To move the tabs into the finance-portal-style left sidebar, the
 * state is lifted into this context: the Sidebar drives it, the Dashboard
 * reads it. Nav item computation (feature flags + access) stays in the
 * Sidebar via `useRegistryConfig` + `useAuth`, so nothing else needs lifting.
 */

export type ViewKey =
  | 'discover'
  | 'servers'
  | 'agents'
  | 'skills'
  | 'virtual'
  | 'external'
  | `custom:${string}`;

interface ViewNavContextValue {
  viewFilter: ViewKey;
  setViewFilter: (view: ViewKey) => void;
}

const ViewNavContext = createContext<ViewNavContextValue | undefined>(undefined);

export const ViewNavProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [viewFilter, setViewFilter] = useState<ViewKey>('discover');
  return (
    <ViewNavContext.Provider value={{ viewFilter, setViewFilter }}>
      {children}
    </ViewNavContext.Provider>
  );
};

export function useViewNav(): ViewNavContextValue {
  const ctx = useContext(ViewNavContext);
  if (!ctx) {
    throw new Error('useViewNav must be used within a ViewNavProvider');
  }
  return ctx;
}
